'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { saveQuickHookIntent } from '@/lib/quickhook-intent';
import { estimateQuickHookProfile } from '@/lib/quickhook-estimate';
import { FEELING_OPTIONS, activityFromFeeling, pickPrimaryFeeling } from '@/lib/quickhook-feeling';
import {
  MEDICAL_OPTIONS,
  normalizeMedicalFlags,
  toggleMedicalFlag,
} from '@/lib/quickhook-medical';
import { GOAL_OPTIONS, pickPrimaryGoal } from '@/lib/quickhook-goals';
import { buildTodayPlanSpec, formatTodayPlanBullets } from '@/lib/quickhook-today-plan';
import EvolutionPreviewPanel from '@/app/components/EvolutionPreviewPanel';
import YourVersionPanel from '@/app/components/YourVersionPanel';
import { resolveGuestVersion } from '@/lib/guest-version-genome';

const TOTAL_STEPS = 4;

const GENDER_OPTIONS = [
  { key: 'female', labelKey: 'gender.female' },
  { key: 'male', labelKey: 'gender.male' },
  { key: 'other', labelKey: 'gender.other' },
];

function toggleInList(list, key) {
  return list.includes(key) ? list.filter((item) => item !== key) : [...list, key];
}

function MultiSelectOption({ selected, label, onToggle }) {
  return (
    <label
      className={`flex min-h-[3rem] w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-3 text-left text-sm font-semibold transition-all ${
        selected
          ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100 shadow-[0_0_20px_rgba(6,182,212,0.45)]'
          : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-cyan-500 hover:text-cyan-200'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
          selected
            ? 'border-cyan-300 bg-cyan-400 text-slate-950'
            : 'border-slate-500 bg-slate-950/80 text-transparent'
        }`}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
      <span className="min-w-0 flex-1 leading-snug">{label}</span>
    </label>
  );
}

export default function QuickClinicalHook({ locale = 'es', autoFocus = false }) {
  const t = useTranslations('QuickHook');
  const rootRef = useRef(null);
  const [step, setStep] = useState(1);
  const [goals, setGoals] = useState([]);
  const [feelings, setFeelings] = useState([]);
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [medicalFlags, setMedicalFlags] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!autoFocus || !rootRef.current) return;
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash === '#primer-paso') {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [autoFocus]);

  const progress = useMemo(() => {
    if (isComplete) return 100;
    if (isCalculating) return 94;
    if (step === 1) return 22;
    if (step === 2) return 44;
    if (step === 3) return 66;
    return 86;
  }, [isCalculating, isComplete, step]);

  function goToNextStep() {
    if (step === 1 && goals.length > 0) {
      setStep(2);
      return;
    }
    if (step === 2 && feelings.length > 0) {
      setStep(3);
      return;
    }
    if (step === 3) {
      const parsedAge = Number(age);
      if (Number.isFinite(parsedAge) && parsedAge >= 14 && parsedAge <= 100 && gender) {
        setStep(4);
      }
    }
  }

  function startCalculation() {
    const parsedAge = Number(age);
    if (medicalFlags.length === 0) return;

    const medical = normalizeMedicalFlags(medicalFlags);
    if (
      !Number.isFinite(parsedAge) ||
      parsedAge < 14 ||
      parsedAge > 100 ||
      !gender ||
      goals.length === 0 ||
      feelings.length === 0
    ) {
      return;
    }

    const goal = pickPrimaryGoal(goals);
    const feeling = pickPrimaryFeeling(feelings);
    const activity = activityFromFeeling(feelings);
    const profileEstimate = estimateQuickHookProfile({
      age: parsedAge,
      gender,
      activity,
      goal,
      goals,
    });
    const guestVersion = resolveGuestVersion({
      goal,
      goals,
      feeling,
      feelings,
      age: parsedAge,
      gender,
      medicalFlags: medical,
    });
    const planSpec = buildTodayPlanSpec({
      goal,
      goals,
      feeling,
      feelings,
      age: parsedAge,
      medicalFlags: medical,
    });

    saveQuickHookIntent({
      goal,
      goals,
      feeling,
      feelings,
      activity,
      age: parsedAge,
      gender,
      medicalFlags: medical,
      source: 'quickhook',
      locale,
      estimate: profileEstimate,
      guestVersion,
      planSpec,
    });

    setEstimate({
      ...profileEstimate,
      age: parsedAge,
      planSpec,
      medicalFlags: medical,
      goals,
      feelings,
      goal,
      feeling,
    });
    setIsCalculating(true);
    window.setTimeout(() => {
      setIsCalculating(false);
      setIsComplete(true);
    }, 1100);
  }

  const todayBullets = useMemo(() => {
    if (!estimate?.planSpec) return [];
    return formatTodayPlanBullets(t, estimate.planSpec);
  }, [estimate, t]);

  const feelingLabels = useMemo(() => {
    const list = estimate?.feelings?.length ? estimate.feelings : feelings;
    return list.map((key) => t(`feeling.${key}`)).join(' · ');
  }, [estimate, feelings, t]);

  const goalLabels = useMemo(() => {
    const list = estimate?.goals?.length ? estimate.goals : goals;
    return list
      .map((key) => {
        const option = GOAL_OPTIONS.find((item) => item.key === key);
        return option ? t(option.labelKey) : key;
      })
      .join(' · ');
  }, [estimate, goals, t]);

  return (
    <div
      id="primer-paso"
      ref={rootRef}
      className="mx-auto w-full max-w-4xl scroll-mt-20 rounded-2xl border border-cyan-500/25 bg-[#020617]/75 p-4 shadow-[0_0_28px_rgba(6,182,212,0.12)] backdrop-blur-md sm:p-5 md:p-6"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0 text-left">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-300">{t('badge')}</p>
          <h3 className="text-lg font-black text-white sm:text-xl">{t('title')}</h3>
        </div>
        <span className="shrink-0 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-bold text-cyan-300 shadow-[0_0_14px_rgba(6,182,212,0.22)]">
          {t('stepBadge', { current: isComplete ? TOTAL_STEPS : step, total: TOTAL_STEPS })}
        </span>
      </div>

      <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-900/80">
        <div
          className="h-1.5 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500 shadow-[0_0_16px_rgba(6,182,212,0.45)] transition-[width] duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {!isCalculating && !isComplete && (
        <div className="space-y-4 text-left">
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold text-slate-200">{t('step1.title')}</p>
                <p className="mt-2 inline-flex rounded-lg border border-cyan-400/40 bg-cyan-500/15 px-2.5 py-1 text-[11px] font-bold tracking-wide text-cyan-200">
                  {t('step1.multiBadge')}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate-300">{t('step1.hint')}</p>
                {goals.length > 0 ? (
                  <p className="mt-1.5 text-xs font-semibold text-emerald-300">
                    {t('step1.selectedCount', { count: goals.length })}
                  </p>
                ) : null}
              </div>
              <div
                role="group"
                aria-label={t('step1.title')}
                className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3"
              >
                {GOAL_OPTIONS.map((option) => (
                  <MultiSelectOption
                    key={option.key}
                    selected={goals.includes(option.key)}
                    label={t(option.labelKey)}
                    onToggle={() => setGoals((prev) => toggleInList(prev, option.key))}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={goToNextStep}
                disabled={goals.length === 0}
                className="landing-cta-pulse mt-2 w-full rounded-xl border border-cyan-500 bg-cyan-500/25 py-3 text-sm font-bold text-cyan-50 transition hover:bg-cyan-500/35 hover:shadow-[0_0_18px_rgba(6,182,212,0.55)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
              >
                {t('buttons.continue')}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold text-slate-200">{t('step2.title')}</p>
                <p className="mt-2 inline-flex rounded-lg border border-cyan-400/40 bg-cyan-500/15 px-2.5 py-1 text-[11px] font-bold tracking-wide text-cyan-200">
                  {t('step2.multiBadge')}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-slate-300">{t('step2.hint')}</p>
                {feelings.length > 0 ? (
                  <p className="mt-1.5 text-xs font-semibold text-emerald-300">
                    {t('step2.selectedCount', { count: feelings.length })}
                  </p>
                ) : null}
              </div>
              <div
                role="group"
                aria-label={t('step2.title')}
                className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3"
              >
                {FEELING_OPTIONS.map((option) => (
                  <MultiSelectOption
                    key={option.key}
                    selected={feelings.includes(option.key)}
                    label={t(option.labelKey)}
                    onToggle={() => setFeelings((prev) => toggleInList(prev, option.key))}
                  />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="rounded-xl border border-slate-600 bg-slate-900/60 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500 hover:text-cyan-300"
                >
                  {t('buttons.back')}
                </button>
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={feelings.length === 0}
                  className="landing-cta-pulse rounded-xl border border-cyan-500 bg-cyan-500/25 py-3 text-sm font-bold text-cyan-50 transition hover:bg-cyan-500/35 hover:shadow-[0_0_18px_rgba(6,182,212,0.55)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
                >
                  {t('buttons.continue')}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-200">{t('step3.title')}</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t('step3.ageLabel')}
                  </span>
                  <input
                    type="number"
                    min="14"
                    max="100"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder={t('step3.agePlaceholder')}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 px-3 py-2.5 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:shadow-[0_0_15px_rgba(6,182,212,0.45)]"
                  />
                </label>
                <div className="space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t('step3.genderLabel')}
                  </span>
                  <div className="grid grid-cols-3 gap-2">
                    {GENDER_OPTIONS.map((option) => {
                      const selected = gender === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setGender(option.key)}
                          className={`rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all sm:text-sm ${
                            selected
                              ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100'
                              : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-cyan-500'
                          }`}
                        >
                          {t(option.labelKey)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="rounded-xl border border-slate-600 bg-slate-900/60 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500 hover:text-cyan-300"
                >
                  {t('buttons.back')}
                </button>
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={
                    !(Number.isFinite(Number(age)) && Number(age) >= 14 && Number(age) <= 100 && gender)
                  }
                  className="landing-cta-pulse rounded-xl border border-cyan-500 bg-cyan-500/25 py-3 text-sm font-bold text-cyan-50 transition hover:bg-cyan-500/35 hover:shadow-[0_0_18px_rgba(6,182,212,0.55)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
                >
                  {t('buttons.continue')}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-bold text-slate-200">{t('step4.title')}</p>
                <p className="mt-1 text-xs text-slate-400">{t('step4.subtitle')}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {MEDICAL_OPTIONS.map((option) => {
                  const selected = medicalFlags.includes(option.key);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setMedicalFlags((prev) => toggleMedicalFlag(prev, option.key))}
                      className={`min-h-[3rem] rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-all ${
                        selected
                          ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100 shadow-[0_0_20px_rgba(6,182,212,0.55)]'
                          : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-cyan-500 hover:text-cyan-200'
                      }`}
                    >
                      {t(option.labelKey)}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] leading-relaxed text-slate-500">{t('step4.privacy')}</p>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="rounded-xl border border-slate-600 bg-slate-900/60 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500 hover:text-cyan-300"
                >
                  {t('buttons.back')}
                </button>
                <button
                  type="button"
                  onClick={startCalculation}
                  disabled={medicalFlags.length === 0}
                  className="landing-cta-pulse rounded-xl border border-cyan-500 bg-cyan-500/25 py-3 text-sm font-bold text-cyan-50 transition hover:bg-cyan-500/35 hover:shadow-[0_0_18px_rgba(6,182,212,0.55)] disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
                >
                  {t('buttons.calculate')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isCalculating && (
        <div className="rounded-2xl border border-cyan-500/30 bg-[#020617]/80 p-5 text-center shadow-[0_0_24px_rgba(6,182,212,0.16)]">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/10">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-300 border-t-transparent" />
          </div>
          <p className="text-base font-bold text-cyan-200 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]">
            {t('loading.title')}
          </p>
          <p className="mt-1 text-sm text-slate-300">{t('loading.description')}</p>
        </div>
      )}

      {isComplete && estimate && (
        <div className="space-y-4 rounded-2xl border border-cyan-500/30 bg-[#020617]/85 p-5 text-left shadow-[0_0_24px_rgba(6,182,212,0.14)]">
          <div className="text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-300">
              {t('result.badge')}
            </p>
            <h4 className="mt-1 text-xl font-black text-white">{t('result.title')}</h4>
            <p className="mt-2 text-sm text-slate-200">{t('result.description')}</p>
            {goalLabels ? (
              <p className="mt-2 inline-flex max-w-full flex-wrap justify-center rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-200">
                {t('result.goalsPrefix')}: {goalLabels}
              </p>
            ) : null}
            {feelingLabels ? (
              <p className="mt-2 inline-flex max-w-full flex-wrap justify-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs font-bold text-cyan-200">
                {t('result.feelingPrefix')}: {feelingLabels}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">
              {t('result.todayPlanLabel')}
            </p>
            <ul className="space-y-2">
              {todayBullets.map((bullet) => (
                <li
                  key={bullet}
                  className="rounded-xl border border-cyan-500/25 bg-cyan-500/5 px-3 py-2.5 text-sm text-slate-100"
                >
                  {bullet}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">{t('result.bmrLabel')}</p>
              <p className="mt-1 text-2xl font-black text-white">{estimate.bmr}</p>
              <p className="text-xs text-slate-400">{t('result.kcalDay')}</p>
            </div>
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-cyan-300">{t('result.tdeeLabel')}</p>
              <p className="mt-1 text-2xl font-black text-cyan-100">{estimate.tdee}</p>
              <p className="text-xs text-cyan-200/70">{t('result.kcalDay')}</p>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-emerald-300">
                {t('result.targetLabel')}
              </p>
              <p className="mt-1 text-2xl font-black text-emerald-100">
                {estimate.calorie_low}–{estimate.calorie_high}
              </p>
              <p className="text-xs text-emerald-200/70">{t('result.kcalDay')}</p>
            </div>
          </div>

          <YourVersionPanel
            goal={estimate.goal}
            goals={estimate.goals || goals}
            feeling={estimate.feeling}
            feelings={estimate.feelings || feelings}
            age={estimate?.age || Number(age) || 40}
            gender={gender}
            medicalFlags={estimate.medicalFlags || medicalFlags}
          />

          <EvolutionPreviewPanel
            goal={estimate.goal}
            goals={estimate.goals || goals}
            feeling={estimate.feeling}
            feelings={estimate.feelings || feelings}
            age={estimate?.age || Number(age) || 40}
          />

          <p className="text-center text-xs text-slate-400">{t('result.disclaimer')}</p>
          <p className="text-center text-sm font-medium text-cyan-100/90">{t('result.saveHint')}</p>

          <div className="grid grid-cols-1 gap-2 pt-1">
            <Link
              href={`/${locale}/register?from=quickhook`}
              className="inline-flex w-full items-center justify-center rounded-xl border border-cyan-400 bg-gradient-to-r from-cyan-500/85 to-blue-500/85 px-4 py-3 text-sm font-black uppercase tracking-wide text-cyan-50 shadow-[0_0_24px_rgba(6,182,212,0.45)] transition hover:scale-[1.01] hover:shadow-[0_0_30px_rgba(6,182,212,0.7)]"
            >
              {t('cta_save_version')}
            </Link>
            <button
              type="button"
              onClick={() => {
                setIsComplete(false);
                setEstimate(null);
                setStep(1);
              }}
              className="inline-flex w-full items-center justify-center rounded-xl border border-slate-600 bg-transparent px-4 py-2.5 text-sm font-semibold text-slate-300 transition hover:border-cyan-500 hover:text-cyan-200"
            >
              {t('buttons.back')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
